import React, { useRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { SectionWrapper } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { RichTextParser } from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers/stringHelpers';
import { FaqsItemPropTypes } from '../../../../propTypes/faqs';
import { BrandContext } from '../../../../contextProviders/brandContext';
import { FeatureFlagContext } from '../../../../contextProviders/featureFlagContext';
import { useGetCmsTagsQuery } from '../../../../data/kontentAi';
import { replacePlaceholders } from '../../../../services/cms/HtmlResolver';
import './questionPanel.scss';
import { createAlias } from '../../../../utils/alias';
import { scrollElementIntoView } from '../../../../utils/scrollUtils';
import { getGtmService } from '../../../../services/gtmService/GtmService';

export const QuestionPanel = ({ item, redirectToQuestion, index, onQuestionToggle }) => {
  const { activeBrandContext } = useContext(BrandContext);
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isKontentAiPreviewEnabled = cspConfiguration?.IsKontentAIPreviewEnabled || false;
  const panelRef = useRef();
  const { question, answer } = item;
  const { data: cmsTags } = useGetCmsTagsQuery({
    brandPartner: activeBrandContext.brand.key,
    previewEnabled: isKontentAiPreviewEnabled
  });
  const questionAlias = createAlias(question);
  const cmsTagPattern = /\[\[\s*([^\]]+)\s*\]\]/g; // Replace "[[key]]" placeholders in your HTML

  useEffect(() => {
    if (redirectToQuestion === questionAlias) {
      scrollElementIntoView(panelRef?.current);
    }
  }, [redirectToQuestion, questionAlias]);

  // Answer bodies are CMS rich text rendered by RichTextParser, so their links can't take a
  // React handler individually. Delegating from the panel keeps the scope tight: only anchors
  // inside an answer body qualify, so links elsewhere on the page can't be misattributed, and
  // GTM needs no anchor-text trigger broad enough to catch them by accident.
  const handleAnswerClick = (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || !link.closest('.accordionSectionContent')) return;

    getGtmService().pushFaqAnswerLinkClick({
      faqQuestion: question,
      // textContent, not innerText: innerText depends on layout, so it is undefined outside a
      // real browser. Rich text carries source indentation, hence the whitespace collapse.
      linkText: link.textContent?.trim().replace(/\s+/g, ' '),
      linkUrl: link.getAttribute('href')
    });
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      ref={panelRef}
      onClick={handleAnswerClick}
    >
      <SectionWrapper
        id={`faq-answer-${index}`}
        customCssClass="faqQuestionPanel"
        isOpen={redirectToQuestion === questionAlias}
        headerButtonCustomLabel={question}
        onChange={onQuestionToggle ? (isOpen) => onQuestionToggle({ question, isOpen }) : undefined}
      >
        {RichTextParser(replacePlaceholders(answer, cmsTags, cmsTagPattern) || '')}
      </SectionWrapper>
    </div>
  );
};

QuestionPanel.propTypes = {
  item: FaqsItemPropTypes,
  redirectToQuestion: PropTypes.string,
  index: PropTypes.number,
  onQuestionToggle: PropTypes.func
};

export default QuestionPanel;
